import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { useUserStore } from '../stores/user'
import { ensureActivePackage, useCanSee } from '../composables/usePackageGate'
import { startPresence } from '../services/presence'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/menu/:slug',
    name: 'public-menu',
    component: () => import('../pages/public/PublicMenu.vue'),
    meta: { public: true, hideSpinner: true },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'analytic',
        path: 'analytic',
        component: () => import('../pages/admin/Analytics.vue'),
        meta: { requiresAuth: true, perm: 'analytic', pkg: 'analytics' },
      },
      {
        name: 'menu',
        path: 'menu',
        component: () => import('../pages/menus/Index.vue'),
        meta: { requiresAuth: true, perm: 'menu.read', pkg: 'menus' },
      },
      {
        name: 'category',
        path: 'category',
        component: () => import('../pages/categories/Index.vue'),
        meta: { requiresAuth: true, perm: 'category.read', pkg: 'categories' },
      },
      {
        name: 'product',
        path: 'product',
        component: () => import('../pages/products/Index.vue'),
        meta: { requiresAuth: true, perm: 'product.read', pkg: 'products' },
      },
      {
        name: 'stock',
        path: 'stock',
        component: () => import('../pages/stocks/Index.vue'),
        meta: { requiresAuth: true, perm: 'stock.read', pkg: 'stocks' },
      },
      {
        name: 'stock-movements',
        path: 'stock-movements',
        component: () => import('../pages/stocks/StockMovements.vue'),
        meta: { requiresAuth: true, perm: 'stock.movement.read', pkg: 'stocks' },
      },
      {
        name: 'option',
        path: 'option',
        component: () => import('../pages/options/Index.vue'),
        meta: { requiresAuth: true, perm: 'option.read', pkg: 'options' },
      },
      {
        name: 'table',
        path: 'table',
        component: () => import('../pages/tables/Index.vue'),
        meta: { requiresAuth: true, perm: 'table.read', pkg: 'tables' },
      },
      {
        name: 'pendingTicket',
        path: 'pendingTicket',
        component: () => import('../pages/tickets/KitchenQueue.vue'),
        meta: { requiresAuth: true, perm: 'ticket.pending', pkg: 'tickets' },
      },
      {
        name: 'ticket',
        path: 'ticket',
        component: () => import('../pages/tickets/Index.vue'),
        meta: { requiresAuth: true, perm: 'ticket.read', pkg: 'tickets' },
      },
      {
        path: '/ticket/:id',
        name: 'ticket-detail',
        component: () => import('../pages/tickets/Detail.vue'),
        meta: { requiresAuth: true, perm: 'table.read', pkg: 'tickets' },
      },
      {
        path: '/tickets/:id/add-products',
        name: 'ticket-add-products',
        component: () => import('../pages/tickets/AddProducts.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAdmin: true },
      },
      {
        name: 'role',
        path: 'role',
        component: () => import('../pages/roles/Index.vue'),
        meta: { requiresAuth: true, perm: 'role.read', pkg: 'roles' },
      },
      {
        path: 'role/:id',
        name: 'role-permissions',
        component: () => import('../pages/roles/Edit.vue'),
        meta: {
          requiresAuth: true,
          icon: 'mso-shield_person',
          displayName: 'Rol İzinleri',
          perm: 'role.update',
          pkg: 'roles',
        },
      },
      {
        path: 'user/:id',
        name: 'user-edit',
        component: () => import('../pages/users/Edit.vue'),
        meta: {
          requiresAuth: true,
          icon: 'mso-shield_person',
          displayName: 'Kullanıcı',
          perm: 'user.update',
          pkg: 'users',
        },
      },
      {
        name: 'user',
        path: 'user',
        component: () => import('../pages/users/Index.vue'),
        meta: { requiresAuth: true, perm: 'user.read', pkg: 'users' },
      },
      {
        name: 'feedback',
        path: 'feedback',
        component: () => import('../pages/feedback/FeedbackList.vue'),
        meta: { requiresAuth: true, perm: 'feedback.read', pkg: 'feedbacks' },
      },
      {
        name: 'campaign-codes',
        path: 'campaign-codes',
        component: () => import('../pages/campaign-codes/Index.vue'),
        meta: { requiresAuth: true, perm: 'campaigncodes.read', pkg: 'campaigncodes' },
      },
    ],
  },
  {
    path: '/auth/',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: {
          public: true,
        },
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
        meta: {
          public: true,
        },
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to) => {
  const needsAuthData = to.matched.some((r) => r.meta?.requiresAuth || r.meta?.requiresAdmin || r.meta?.perm)
  const store = useUserStore()
  const goingToAuthPublic = to.name === 'login' || to.name === 'signup'
  if (goingToAuthPublic && store.isAuthenticated && !to.query?.reauth) {
    return { name: 'dashboard' }
  }

  if (needsAuthData) {
    await store.ensureInitialized()

    if (!store.isAuthenticated) {
      const ok = await store.renewSilently()
      if (!ok) {
        return { name: 'login', query: { redirect: to.fullPath } }
      }
    }
    startPresence()
    await ensureActivePackage()
  }

  const requiredPerm = to.meta?.perm as string | undefined
  if (requiredPerm && !store.permissions.includes(requiredPerm as any)) {
    return { name: 'dashboard' }
  }

  if (to.meta?.requiresAdmin && !store.hasRole('Admin')) {
    return { name: 'dashboard' }
  }

  const pkgKey = to.meta?.pkg as string | undefined
  if (pkgKey && !useCanSee(pkgKey).value) {
    return { name: 'dashboard' }
  }

  return true
})

export default router
